import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCrmActivityTracking } from '../../hooks/useCrmActivityTracking';
import { useCrmSessionBeacon } from '../../hooks/useCrmSessionBeacon';
import { useProjectTimeContext } from '../../context/ProjectTimeContext';
import { useLeadTimeContext } from '../../context/LeadTimeContext';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { resolveCrmActivityTimezone } from '../../utils/crmActivityHours';
import { classifyCrmActivityLocation, leadDisplayName } from '../../utils/crmActivityContext';
import { isCrmStaffUser } from '../../utils/userRoles';

/**
 * Silent mount: records CRM presence + CEO session heartbeats for internal staff only.
 * Heartbeats always include jobId/dealId when present (even while Project/Lead timers write).
 */
const CrmActivityTracker: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers, timberProjects, leads } = useCRM();
  const location = useLocation();
  const { activeProjectId } = useProjectTimeContext();
  const { activeLeadId, activeDealId, activeProposalId } = useLeadTimeContext();

  const is1099 = user?.is1099Contractor === true;
  const staff = isCrmStaffUser(user?.role);
  const enabled = staff && !is1099;

  const timezone = resolveCrmActivityTimezone(
    crmUsers.find((u) => u.uid === user?.id)?.timezone,
  );

  const projectName = timberProjects.find((p) => p.id === activeProjectId)?.name;
  const lead = leads.find((l) => l.id === activeLeadId);
  const context = useMemo(
    () =>
      classifyCrmActivityLocation({
        pathname: location.pathname,
        search: location.search,
        activeProjectId,
        activeLeadId,
        activeDealId,
        activeProposalId,
        projectName,
        leadName: lead ? leadDisplayName(lead) : undefined,
      }),
    [
      location.pathname,
      location.search,
      activeProjectId,
      activeLeadId,
      activeDealId,
      activeProposalId,
      projectName,
      lead,
    ],
  );

  const projectTrackingActive = Boolean(activeProjectId) && staff && !is1099;
  const leadTrackingActive =
    Boolean(activeLeadId)
    && (user?.role === 'employee' || user?.role === 'admin')
    && !is1099;

  useCrmActivityTracking({
    userId: user?.id,
    userName: user?.name,
    userEmail: user?.email,
    timezone,
    enabled,
    context,
    skipSessionWrite: projectTrackingActive || leadTrackingActive,
    projectTrackingActive,
    leadTrackingActive,
  });

  useCrmSessionBeacon({
    userId: user?.id,
    userEmail: user?.email,
    userName: user?.name,
    enabled,
    context,
  });

  return null;
};

export default CrmActivityTracker;
